import { Effect } from 'effect';
import { applyBrandToSeoEffect, brandCopyEffect } from './copy';
import { getBrandEffect } from './resolve';
import type { BrandConfig } from './types';

export interface PageSeo {
  title?: string;
  description?: string;
  canonical?: string;
}

/**
 * An open document holds the tab (W.4.132). The page keeps saying what it is
 * called and its title waits here until the document goes, so this file alone
 * decides what the tab shows and the order of the two no longer matters. Back
 * or Forward can commit a new page's title while the old Studio is still
 * mounted behind a Suspense fallback; that Studio then unmounts, or comes back.
 */
interface DocumentTabHold {
  pageTitle: string;
  pinPageTitle: (title: string | undefined) => void;
}
let hold: DocumentTabHold | null = null;

function writePageTitle(title: string): void {
  if (hold) {
    hold.pageTitle = title;
    hold.pinPageTitle(title);
  } else {
    document.title = title;
  }
}

/**
 * The only sanctioned client-side SEO write path. Pass canonical okraPDF
 * strings; the brand boundary rewrites them for branded deploys (identity
 * for okrapdf). Creates the description/canonical tags when missing.
 */
export const setPageSeoEffect = Effect.fn('web.brand.setPageSeo')(function* (
  seo: PageSeo,
  brand?: BrandConfig,
) {
  if (typeof document === 'undefined') return;
  const resolved = brand ?? (yield* getBrandEffect());
  const branded = yield* applyBrandToSeoEffect(seo, resolved);

  if (branded.title !== undefined) {
    writePageTitle(branded.title);
  }

  if (branded.description !== undefined) {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = branded.description;
  }

  if (branded.canonical !== undefined) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = branded.canonical;
  }
});

export function setPageSeo(seo: PageSeo, brand?: BrandConfig): void {
  Effect.runSync(setPageSeoEffect(seo, brand));
}

/**
 * `Documents — okraPDF`, `sample.pdf — okraPDF` (W.4.132). Only the suffix
 * crosses the brand boundary, so a file name stays as the user typed it.
 */
const tabTitleEffect = Effect.fn('web.brand.tabTitle')(function* (
  label: string,
  brand?: BrandConfig,
) {
  const resolved = brand ?? (yield* getBrandEffect());
  return `${label} — ${yield* brandCopyEffect('okraPDF', resolved)}`;
});

/** Tab title of an app page. Public pages use setPageSeo. */
export const setTabTitleEffect = Effect.fn('web.brand.setTabTitle')(function* (
  label: string,
  brand?: BrandConfig,
) {
  if (typeof document === 'undefined') return;
  writePageTitle(yield* tabTitleEffect(label, brand));
});

export function setTabTitle(label: string, brand?: BrandConfig): void {
  Effect.runSync(setTabTitleEffect(label, brand));
}

/**
 * Puts the open document in the tab, or with `null` gives the tab back to the
 * page. `pinPageTitle` is for a reader of document.title that must not see a
 * file name: it hears the page's title before the name lands and whenever the
 * page changes, then `undefined` once the name has left.
 */
export const setDocumentTabTitleEffect = Effect.fn('web.brand.setDocumentTabTitle')(function* (
  name: string | null,
  pinPageTitle: (title: string | undefined) => void = () => {},
  brand?: BrandConfig,
) {
  if (typeof document === 'undefined') return;
  if (name === null) {
    if (!hold) return;
    document.title = hold.pageTitle;
    hold.pinPageTitle(undefined);
    hold = null;
    return;
  }
  const title = yield* tabTitleEffect(name, brand);
  if (!hold) {
    hold = { pageTitle: document.title, pinPageTitle };
    pinPageTitle(hold.pageTitle);
  }
  document.title = title;
});

export function setDocumentTabTitle(
  name: string | null,
  pinPageTitle?: (title: string | undefined) => void,
  brand?: BrandConfig,
): void {
  Effect.runSync(setDocumentTabTitleEffect(name, pinPageTitle, brand));
}
